import { useState, useEffect, useMemo } from 'react';
import { useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import type {
	AgriculturalDefensive,
	Fertilizer,
	Manufacturer,
	ProductsTab,
	ProductsPermissions,
} from '../types';
import { useGetDefensives } from '../../api/hooks/defensives/useGetDefensives';
import { useGetUsers } from '../../api/hooks/users/useGetUsers';
import { useGetFertilizers, useGetManufacturers } from '@practium/features/api';
import { FERTILIZER_CATEGORY_OPTIONS } from '../constants/fertilizer.constants';

const PAGE_SIZE = 10;

const FERTILIZER_NATURE_LABELS: Record<string, string> = {
	solid: 'Sólido',
	liquid: 'Líquido',
	solido: 'Sólido',
	liquido: 'Líquido',
};

export interface UseProductsPageProps {
	permissions: ProductsPermissions;
	currentUserId?: string;
	getUserDisplayName?: (userId: string) => string;
	ownedByMeLabel?: string;
	initialTab?: ProductsTab;
}

export const useProductsPage = ({
	permissions,
	currentUserId,
	getUserDisplayName,
	ownedByMeLabel = 'Cliente',
	initialTab = 'defensives',
}: UseProductsPageProps) => {
	const [activeTab, setActiveTabState] = useState<ProductsTab>(initialTab);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);

	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	const debouncedSearchQuery = useDebounce(searchQuery, 400);

	const parseFilters = (values: string[]) => {
		const result: Record<string, string[]> = {};
		for (const v of values) {
			const idx = v.indexOf(':');
			if (idx === -1) continue;
			const field = v.slice(0, idx);
			const val = v.slice(idx + 1);
			if (!result[field]) result[field] = [];
			result[field].push(val);
		}
		return result;
	};

	const parsedFilters = useMemo(() => parseFilters(filterValues), [filterValues]);
	const remoteFilter = debouncedSearchQuery.trim() || undefined;
	const remoteSortAsc = sortAsc ? true : undefined;

	const { data: defensivesData, pagination: defensivesPagination, isLoading: isLoadingDefensives } = useGetDefensives({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'defensives',
		filter: activeTab === 'defensives' ? remoteFilter : undefined,
		manufacturer: activeTab === 'defensives' ? parsedFilters.manufacturer : undefined,
		className: activeTab === 'defensives' ? parsedFilters.class : undefined,
		nature: activeTab === 'defensives' ? parsedFilters.nature : undefined,
		origin: activeTab === 'defensives' ? parsedFilters.origin : undefined,
		sortAsc: activeTab === 'defensives' ? remoteSortAsc : undefined,
	});
	const { data: fertilizersData, pagination: fertilizersPagination, isLoading: isLoadingFertilizers } = useGetFertilizers({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'fertilizers',
		filter: activeTab === 'fertilizers' ? remoteFilter : undefined,
		manufacturer: activeTab === 'fertilizers' ? parsedFilters.manufacturer : undefined,
		category: activeTab === 'fertilizers' ? parsedFilters.category : undefined,
		nature: activeTab === 'fertilizers' ? parsedFilters.nature : undefined,
		origin: activeTab === 'fertilizers' ? parsedFilters.origin : undefined,
		sortAsc: activeTab === 'fertilizers' ? remoteSortAsc : undefined,
	});
	const { data: manufacturersData, pagination: manufacturersPagination, isLoading: isLoadingManufacturers } = useGetManufacturers({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'manufacturers',
		filter: activeTab === 'manufacturers' ? remoteFilter : undefined,
		visibility: activeTab === 'manufacturers' ? parsedFilters.visibility : undefined,
		sortAsc: activeTab === 'manufacturers' ? remoteSortAsc : undefined,
	});
	const { data: filterDefensivesData, isLoading: isLoadingFilterDefensives } = useGetDefensives({
		limit: 300,
	});
	const { data: filterFertilizersData, isLoading: isLoadingFilterFertilizers } = useGetFertilizers({
		limit: 300,
	});
	const { data: filterManufacturersData, isLoading: isLoadingFilterManufacturers } = useGetManufacturers({
		limit: 300,
	});
	const { data: usersData, isLoading: isLoadingUsers } = useGetUsers({
		enabled: !getUserDisplayName,
	});

	const defensives: AgriculturalDefensive[] = defensivesData ?? [];
	const fertilizers: Fertilizer[] = fertilizersData ?? [];
	const manufacturers: Manufacturer[] = manufacturersData ?? [];
	const filterDefensives: AgriculturalDefensive[] = filterDefensivesData ?? [];
	const filterFertilizers: Fertilizer[] = filterFertilizersData ?? [];
	const users = usersData ?? [];

	const isLoading =
		isLoadingDefensives ||
		isLoadingFertilizers ||
		isLoadingManufacturers ||
		isLoadingFilterDefensives ||
		isLoadingFilterFertilizers ||
		isLoadingFilterManufacturers ||
		isLoadingUsers;

	const manufacturerOptions = useMemo(() => (
		filterManufacturersData.map(manufacturer => ({
			value: `manufacturer:${manufacturer._id}`,
			label: manufacturer.tradingName ?? manufacturer.businessName ?? '',
		}))
	), [filterManufacturersData]);

	const userNameById = useMemo(() => {
		return new Map(
			users.map((user) => [
				user._id,
				[user.name, user.surName].filter(Boolean).join(' ').trim() || user.email,
			])
		);
	}, [users]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearchQuery, filterValues, sortAsc, activeTab]);

	const handleTabChange = (tab: ProductsTab) => {
		setActiveTabState(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const defensivesFilterGroups = useMemo<FilterGroup[]>(() => {
		const groups: FilterGroup[] = [];
		const classes = [...new Set(filterDefensives.flatMap(d => d.class ?? []).filter(Boolean) as string[])].sort();
		if (classes.length > 0) {
			groups.push({ label: 'Classe', options: classes.map(c => ({ value: `class:${c}`, label: c })) });
		}
		const natures = [...new Set(filterDefensives.map(d => d.nature).filter(Boolean) as string[])].sort();
		if (natures.length > 0) {
			groups.push({ label: 'Natureza', options: natures.map(n => ({ value: `nature:${n}`, label: FERTILIZER_NATURE_LABELS[n] ?? n })) });
		}
		groups.push({
			label: 'Origem',
			options: [
				{ value: 'origin:admin', label: 'Admin' },
				{ value: 'origin:own', label: ownedByMeLabel },
			],
		});
		groups.push({
			label: 'Fabricante',
			options: manufacturerOptions,
		});
		return groups;
	}, [filterDefensives, ownedByMeLabel, manufacturerOptions]);

	const fertilizersFilterGroups = useMemo<FilterGroup[]>(() => {
		const groups: FilterGroup[] = [];
		groups.push({
			label: 'Categoria',
			options: FERTILIZER_CATEGORY_OPTIONS.map(({ value, label }) => ({
				value: `category:${value}`,
				label,
			})),
		});
		const natures = [...new Set(filterFertilizers.map(f => f.nature).filter(Boolean))].sort();
		if (natures.length > 0) {
			groups.push({
				label: 'Natureza',
				options: natures.map(n => ({ value: `nature:${n}`, label: FERTILIZER_NATURE_LABELS[n] ?? n })),
			});
		}
		groups.push({
			label: 'Origem',
			options: [
				{ value: 'origin:admin', label: 'Admin' },
				{ value: 'origin:own', label: ownedByMeLabel },
			],
		});
		groups.push({
			label: 'Fabricante',
			options: manufacturerOptions,
		});
		return groups;
	}, [filterFertilizers, ownedByMeLabel, manufacturerOptions]);

	const manufacturersFilterGroups = useMemo<FilterGroup[]>(() => [
		{
			label: 'Visibilidade',
			options: [
				{ value: 'visibility:public', label: 'Público' },
				{ value: 'visibility:private', label: 'Privado' },
			],
		},
	], []);

	const filterGroups = useMemo<FilterGroup[]>(() => {
		if (activeTab === 'defensives') return defensivesFilterGroups;
		if (activeTab === 'fertilizers') return fertilizersFilterGroups;
		return manufacturersFilterGroups;
	}, [activeTab, defensivesFilterGroups, fertilizersFilterGroups, manufacturersFilterGroups]);

	const processedDefensives = useMemo(() => {
		return defensives;
	}, [defensives]);

	const processedFertilizers = useMemo(() => {
		return fertilizers;
	}, [fertilizers]);

	const processedManufacturers = useMemo(() => {
		return manufacturers;
	}, [manufacturers]);

	const totalPages = useMemo(() => {
		if (activeTab === 'fertilizers') return fertilizersPagination?.totalPages ?? 1;
		if (activeTab === 'manufacturers') return manufacturersPagination?.totalPages ?? 1;
		return defensivesPagination?.totalPages ?? 1;
	}, [activeTab, defensivesPagination, fertilizersPagination, manufacturersPagination]);

	const filteredDefensives = useMemo(
		() => processedDefensives,
		[processedDefensives]
	);

	const filteredFertilizers = useMemo(
		() => processedFertilizers,
		[processedFertilizers]
	);

	const filteredManufacturers = useMemo(
		() => processedManufacturers,
		[processedManufacturers]
	);

	const resolveUserName = (userId: string | null): string => {
		if (!userId) return 'Admin';
		if (getUserDisplayName) return getUserDisplayName(userId);
		const userName = userNameById.get(userId);
		if (userName) return userName;
		return `Usuário ${userId}`;
	};

	const canEditRecord = (isPublic: boolean): boolean => {
		if (isPublic) return permissions.canEditPublic;
		return permissions.canEdit;
	};

	return {
		activeTab,
		setActiveTab: handleTabChange,
		defensives,
		fertilizers,
		manufacturers,
		filteredDefensives,
		filteredFertilizers,
		filteredManufacturers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
		currentUserId,
		resolveUserName,
		canEditRecord,
	};
};
